import React, { useEffect, useRef } from 'react';
import { Employee } from '@/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { Edit, Trash2, ChevronDown, ChevronUp, ChevronRight } from 'lucide-react';
import { getContractStatusConfig, formatContractStatus } from '@/lib/employees/contract-status';
import AddEmployeeForm from './AddEmployeeForm';
import EditEmployeeForm from './EditEmployeeForm';
import { EmployeePlanProgress } from './EmployeePlanProgress';
import { usePlanFulfillment } from '@/hooks/employees/usePlanFulfillment';
import { useDashboardTab } from '@/context/DashboardTabContext';
import { cn } from '@/lib/core/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface EmployeeTableProps {
  businessId: string | null;
  employees: Employee[];
  loading: boolean;
  onEmployeeAdded: (newEmployee: Employee) => void;
  onEditEmployee: (employee: Employee) => void;
  onEmployeeUpdated: (updatedEmployee: Employee) => void;
  onDeleteEmployee: (employeeId: string) => void;
  onConfirmDeleteEmployee: () => void;
  onSendResetPassword: (email: string) => void;
  onEmployeeClick?: (employee: Employee) => void; // New: Navigate to detail view
  isAddModalOpen: boolean;
  onAddModalOpenChange: (open: boolean) => void;
  isEditModalOpen: boolean;
  onEditModalOpenChange: (open: boolean) => void;
  editingEmployee: Employee | null;
  isDeleteConfirmOpen: boolean;
  onDeleteConfirmOpenChange: (open: boolean) => void;
  employeeToDeleteId: string | null;
  sortColumn: string | null;
  sortDirection: "asc" | "desc";
  onSort: (column: string) => void;
}

/**
 * Get initials from employee name for avatar
 */
const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

/**
 * Generate a consistent color for an employee based on their name
 */
const getAvatarColor = (name: string): string => {
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-orange-500',
    'bg-pink-500',
    'bg-cyan-500',
    'bg-indigo-500',
    'bg-teal-500',
    'bg-rose-500',
    'bg-amber-500',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const EmployeeTable: React.FC<EmployeeTableProps> = ({
  businessId,
  employees,
  loading,
  onEmployeeAdded,
  onEditEmployee,
  onEmployeeUpdated,
  onDeleteEmployee,
  onConfirmDeleteEmployee,
  onSendResetPassword,
  onEmployeeClick,
  isAddModalOpen,
  onAddModalOpenChange,
  isEditModalOpen,
  onEditModalOpenChange,
  editingEmployee,
  isDeleteConfirmOpen,
  onDeleteConfirmOpenChange,
  employeeToDeleteId,
  sortColumn,
  sortDirection,
  onSort,
}) => {
  const { highlightedItem } = useDashboardTab();
  const highlightedRowRef = useRef<HTMLTableRowElement>(null);

  // Fetch plan fulfillment data for all employees
  const employeeIds = employees.map(e => e.id);
  const { fulfillmentData, isLoading: fulfillmentLoading } = usePlanFulfillment(businessId, employeeIds);

  // Scroll to highlighted employee row when it changes
  useEffect(() => {
    if (highlightedItem?.type === 'employee' && highlightedRowRef.current) {
      highlightedRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedItem]);

  const handleRowClick = (employee: Employee) => {
    if (onEmployeeClick) {
      onEmployeeClick(employee);
    }
  };

  if (loading) {
    return <p>Loading employees...</p>;
  }

  if (!businessId) {
    return <p className="text-center text-gray-500 dark:text-gray-400 py-8">Please select a business to view employees.</p>;
  }

  return (
    <div className="space-y-4">
      {/* Add Employee Modal */}
      {businessId && (
        <AddEmployeeForm
          businessId={businessId}
          isOpen={isAddModalOpen}
          onOpenChange={onAddModalOpenChange}
          onEmployeeAdded={onEmployeeAdded}
        />
      )}
      {/* Edit Employee Modal */}
      <EditEmployeeForm
        employee={editingEmployee}
        isOpen={isEditModalOpen}
        onOpenChange={onEditModalOpenChange}
        onEmployeeUpdated={onEmployeeUpdated}
      />
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteConfirmOpen} onOpenChange={onDeleteConfirmOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the employee record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => onDeleteConfirmOpenChange(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmDeleteEmployee} className="bg-red-600 hover:bg-red-700">
              Yes, delete employee
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {employees.length === 0 && !loading ? (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">No employees found for this business.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[250px]">
                <div
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={() => onSort("name")}
                >
                  Employee
                  {sortColumn === "name" &&
                    (sortDirection === "asc" ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    ))}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer"
                onClick={() => onSort("role")}
              >
                Role{" "}
                {sortColumn === "role" &&
                  (sortDirection === "asc" ? (
                    <ChevronUp className="h-4 w-4 inline" />
                  ) : (
                    <ChevronDown className="h-4 w-4 inline" />
                  ))}
              </TableHead>
              <TableHead
                className="cursor-pointer"
                onClick={() => onSort("employment_status")}
              >
                Status{" "}
                {sortColumn === "employment_status" &&
                  (sortDirection === "asc" ? (
                    <ChevronUp className="h-4 w-4 inline" />
                  ) : (
                    <ChevronDown className="h-4 w-4 inline" />
                  ))}
              </TableHead>
              <TableHead>Plan Progress</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {employees.map((employee) => {
              const isHighlighted = highlightedItem?.type === 'employee' && highlightedItem?.id === employee.id;
              const fulfillment = fulfillmentData.get(employee.id);
              const avatarColor = getAvatarColor(employee.name);

              return (
                <TableRow
                  key={employee.id}
                  ref={isHighlighted ? highlightedRowRef : null}
                  className={cn(
                    "cursor-pointer transition-all duration-200 hover:bg-muted/50",
                    isHighlighted && "ring-2 ring-primary bg-primary/10 animate-pulse"
                  )}
                  onClick={() => handleRowClick(employee)}
                >
                  {/* Employee with Avatar */}
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className={cn("h-9 w-9 text-white", avatarColor)}>
                        <AvatarFallback className={cn("text-xs font-medium", avatarColor)}>
                          {getInitials(employee.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="font-medium">{employee.name}</span>
                        <span className="text-xs text-muted-foreground">{employee.email}</span>
                      </div>
                    </div>
                  </TableCell>

                  {/* Role */}
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm">{employee.roleInfo?.name || 'No Role'}</span>
                      {employee.employmentType && (
                        <Badge variant="outline" className="text-[10px] w-fit">
                          {employee.employmentType.label_native}
                        </Badge>
                      )}
                    </div>
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Badge
                      className={`${getContractStatusConfig(employee.contract_status || 'active').bgColor} ${getContractStatusConfig(employee.contract_status || 'active').textColor}`}
                    >
                      {formatContractStatus(employee.contract_status || 'active')}
                    </Badge>
                  </TableCell>

                  {/* Plan Progress */}
                  <TableCell>
                    <EmployeePlanProgress fulfillment={fulfillment} />
                  </TableCell>

                  {/* Actions */}
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onEditEmployee(employee)}
                      >
                        <Edit className="h-4 w-4" />
                        <span className="sr-only">Edit Employee</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => onDeleteEmployee(employee.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete Employee</span>
                      </Button>
                      {onEmployeeClick && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleRowClick(employee)}
                        >
                          <ChevronRight className="h-4 w-4" />
                          <span className="sr-only">View Details</span>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default EmployeeTable;
